import { Component, OnInit, inject } from '@angular/core';
import { AuthenticationMethodType, PollResponse } from '../../../backend/model';
import { AuthenticationContextDatasourceService } from '../../../backend/datasource';
import { UserAgentUtil } from '../../util';
import { timer } from 'rxjs';
import { FrejaEidAuthenticationTicket } from '../../../backend/model/freja-eid-authentication-ticket';
import { FrejaEidService } from '../../../backend/api/freja-eid.service';
import { FrejaEidPollingService } from '../../../backend/datasource/freja-eid-polling.service';
import { ThemeHandlerService } from '../../service/theme-handler.service';
import { Theme } from '../../../backend/model/theme.model';
import { LoadingSpinnerComponent } from '../../../shared/component/loading-spinner/loading-spinner.component';
import { IDCardModule } from '@inera/ids-angular/components/card';
import { IDLinkModule } from '@inera/ids-angular/components/link';
import { NgTemplateOutlet } from '@angular/common';
import { ThemeType } from '../../../backend/model/theme-type';

@Component({
  selector: 'freja-eid-authentication',
  imports: [LoadingSpinnerComponent, IDCardModule, IDLinkModule, NgTemplateOutlet],
  templateUrl: './freja-eid-authentication.component.html',
  styleUrls: ['./freja-eid-authentication.component.scss']
})
export class FrejaEidAuthenticationComponent implements OnInit {
  private authenticationContextDatasource = inject(AuthenticationContextDatasourceService);
  private frejaEidPollingService = inject(FrejaEidPollingService);
  private frejaEidService = inject(FrejaEidService);
  private themeHandler = inject(ThemeHandlerService);

  public base64Prefix = 'data:image/jpeg;base64,';
  public ticket: FrejaEidAuthenticationTicket;
  public pending = false;
  public qrToken = null;
  public qrTokenB64 = null;
  public displayQrCode = true;

  public isSameDevice = false;
  public isAndroid = false;
  public isStarting = false;

  public theme: Theme;
  public themeType = ThemeType;
  public clientInfo;

  ngOnInit(): void {
    this.isSameDevice =
      this.authenticationContextDatasource.getAuthMethodType() === AuthenticationMethodType.FREJA_EID_SAME_DEVICE;

    this.themeHandler.getTheme().subscribe(theme => {
      this.theme = theme.value.value;
    });

    this.clientInfo = this.authenticationContextDatasource.getClientInfo();

    this.getTicket();

    this.isAndroid = UserAgentUtil.isAndroid();
  }

  public getMethodTypeText(): string {
    return this.authenticationContextDatasource.getIsSignature() ? 'underskriften' : 'inloggningen';
  }

  public cancelAuthentication(): void {
    this.frejaEidService.cancelAuthentication(
      this.authenticationContextDatasource.getSessionId(),
      this.authenticationContextDatasource.getIdp()
    );
  }

  public getTypeText(): string {
    return this.authenticationContextDatasource.getIsSignature() ? 'Jag signerar hos' : 'Jag legitimerar mig hos';
  }

  public manualAutostart(): void {
    this.displayQrCode = false;
    this.autoStart(true);
  }

  public isSingleMethod(): boolean {
    return this.authenticationContextDatasource.getIsSingleMethod();
  }

  private getTicket(): void {
    this.frejaEidService
      .authenticationRequest(
        this.authenticationContextDatasource.getAuthMethodType(),
        this.authenticationContextDatasource.getSessionId(),
        this.authenticationContextDatasource.getIdp()
      )
      .subscribe(ticket => {
        this.ticket = ticket;
        this.frejaEidService.setTicket(this.ticket);

        if (ticket.qrStartTokenCodeB64) {
          this.qrTokenB64 = ticket.qrStartTokenCodeB64;
        } else {
          this.qrToken = ticket.autoStartToken;
        }

        this.startPoll();

        if (!this.isAndroid || (this.isAndroid && !this.authenticationContextDatasource.getIsSingleMethod())) {
          this.autoStart(false);
        }
      });
  }

  private autoStart(manualAutoStart: boolean): void {
    if (this.isSameDevice || manualAutoStart) {
      this.tryStart();
    }
  }

  private tryStart(): void {
    if (this.isStarting === true) {
      return;
    }

    this.isStarting = true;

    timer(3000).subscribe(() => {
      this.isStarting = false;
    });
    window.location.href = this.ticket.autoStartToken;
  }

  private startPoll(): void {
    this.frejaEidPollingService.connect(this.authenticationContextDatasource.getSessionId()).subscribe(response => {
      if (this.evaluateIfStartedOrCompleted(response)) {
        if (response?.redirectUrl?.length) {
          window.location.href = response.redirectUrl;
        } else {
          this.pending = true;
        }
      } else {
        if (response?.qrStartTokenCodeB64?.length) {
          this.qrTokenB64 = response.qrStartTokenCodeB64;
        }
      }
    });
  }

  private evaluateIfStartedOrCompleted(data: PollResponse): boolean {
    return data !== null && (data.statusCode === 'DELIVERED_TO_MOBILE' || data.statusCode === 'APPROVED');
  }
}
